/**
 * @memberof activerecord
 * @classdesc
 * Expose an interface to make navigation paginated results easier. Mantains state
 * about ongoing requests and pages. Contains a "set" of information for
 * "params" and "options" to be applied to each request.
 *
 * @property {ActiveRecordSource} source
 * @property {object} params
 * @property {object} opts
 * @property {boolean} isLoading
 * @property {ActiveRecord[]} records
 * @property {number[]} pageNumbers
 * @property {object} meta
 * @property {number} meta.page
 * @property {number} meta.totalRecords
 * @property {number} meta.totalPages
 * @property {number} meta.perPage
 */
class ActiveRecordPagination {
    /**
     * @param {ActiveRecordSource} source
     * @param {object} params
     * @param {object} opts
     */
    constructor(RecordClass, source, params, opts) {
        this.RecordClass = RecordClass;
        this.source = source;
        this.params = params;
        this.opts = opts;
        this.isLoading = false;
        this.records = [];
        this.meta = defaultMeta();
    }
    get pageNumbers() {
        return new Array(this.meta.totalPages).fill(null).map((_, index) => index + 1);
    }
    /**
     * @return {Promise<ActiveRecord[]>}
     */
    next() {
        const pageNumber = Math.min(this.meta.totalPages, this.meta.page + 1);
        if (pageNumber === this.meta.page)
            return Promise.resolve(this.records);
        return this.page(pageNumber);
    }
    /**
     * @return {Promise<ActiveRecord[]>}
     */
    previous() {
        const pageNumber = Math.max(1, this.meta.page - 1);
        if (pageNumber === this.meta.page)
            return Promise.resolve(this.records);
        return this.page(pageNumber);
    }
    /**
     * @param {number} pageNumber
     * @return {Promise<ActiveRecord[]>}
     */
    page(pageNumber) {
        this.isLoading = true;
        // TODO: Validate page number falls within "pageNumbers"
        return this.source.page(pageNumber, this.params, this.opts)
            .then(({ records, meta }) => {
            this.records = records.map((props) => new this.RecordClass(props));
            this.meta = meta || defaultMeta();
            this.isLoading = false;
            return records.map(record => new this.RecordClass(record));
        })
            .catch((error) => {
            this.isLoading = false;
            return Promise.reject(error);
        });
    }
    /**
     * @return {Promise<ActiveRecord[]>}
     */
    first() {
        const pageNumber = 1;
        return this.page(pageNumber);
    }
    /**
     * @return {Promise<ActiveRecord[]>}
     */
    last() {
        const pageNumber = this.meta.totalPages;
        return this.page(pageNumber);
    }
    /**
     * @return {Promise<ActiveRecord[]>}
     */
    refresh() {
        return this.page(this.meta.page);
    }
}
function defaultMeta() {
    return {
        page: 1,
        totalRecords: 0,
        totalPages: 1,
        perPage: 25, // TODO: Make configurable OR at least read from params
    };
}
export default ActiveRecordPagination;
